import React from 'react';
import { makeStyles } from '@mui/styles';
import { AccordionDetails, AccordionSummary, Grid, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ParamTable from '../ParamTable';
import { CodeHighlighter } from '../index';
import { AdminProgressBar } from '../../../../components';

const useStyles = makeStyles((theme) => ({
  pre: {
    whiteSpace: 'pre-wrap',
  },
  childAccordion: {
    display: 'block',
  },
  headingPost: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
    color: theme.palette.success.main,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
}));

const DietsCompare = () => {
  const classes = useStyles();
  return (
    <AccordionDetails className={classes.childAccordion}>
      <Typography>
        <Grid
          container
          item
          xs={12}
          spacing={2}
          justifyContent={'space-between'}
        >
          <Grid
            item
            xs={2}
          >
            <h3>Example Request</h3>
          </Grid>
          <Grid
            item
            xs={10}
          >
            <ParamTable
              rows={[
                { name: 'diet_code', type: 'string', description: 'The short code of a Diet ID Diet found via /diets' },
                { name: 'quality', type: 'integer', description: 'The quality level of a Diet ID Diet.' },
                {
                  name: 'ideal_code',
                  type: 'string',
                  description: 'The short code of a Diet ID Diet found via /diets',
                },
                { name: 'ideal_quality', type: 'integer', description: 'The quality level of a Diet ID Diet.' },
              ]}
            />
          </Grid>
          <Grid
            item
            xs={2}
          >
            <h3>Example Response</h3>
          </Grid>
          <Grid
            item
            xs={10}
          >
            <CodeHighlighter
              code={`{
    "data": {
        "changes": [
            {
                "key": "meta-fruit-juice",
                "label": "Fruit juice",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": true,
                    "suppress_decrease": false,
                    "children": {
                        "FRU0100": {
                            "id": "FRU0100",
                            "label": "Citrus Juice",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "FRU0200": {
                            "id": "FRU0200",
                            "label": "Fruit Juice excluding Citrus Juice",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Fruit Juice",
                    "value": 0.0
                },
                "old_value": 0.0,
                "new_value": 0.2900690467777778,
                "difference": 0.2900690467777778,
                "children": [
                    {
                        "id": "FRU0100",
                        "label": "Citrus juice",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "FRU0100",
                        "old_value": 0.0,
                        "new_value": 0.2900690467777778,
                        "difference": 0.2900690467777778
                    },
                    {
                        "id": "FRU0200",
                        "label": "Fruit juice excluding citrus juice",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "FRU0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    }
                ]
            },
            {
                "key": "meta-fruit",
                "label": "Fruit",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": false,
                    "suppress_decrease": true,
                    "children": {
                        "FRU0300": {
                            "id": "FRU0300",
                            "label": "Citrus Fruit",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": true,
                            "value": 0.0
                        },
                        "FRU0400": {
                            "id": "FRU0400",
                            "label": "Fruit excluding Citrus Fruit",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Fruit",
                    "value": 0.0
                },
                "old_value": 0.0,
                "new_value": 3.946592636444445,
                "difference": 3.946592636444445,
                "children": [
                    {
                        "id": "FRU0300",
                        "label": "Citrus fruit",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": true,
                        "value": 0.0,
                        "key": "FRU0300",
                        "old_value": 0.0,
                        "new_value": 0.1708720275555556,
                        "difference": 0.1708720275555556
                    },
                    {
                        "id": "FRU0400",
                        "label": "Fruit excluding citrus fruit",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "FRU0400",
                        "old_value": 0.0,
                        "new_value": 3.77572060888889,
                        "difference": 3.77572060888889
                    }
                ]
            },
            {
                "key": "meta-vegetables",
                "label": "Vegetables",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": false,
                    "suppress_decrease": true,
                    "children": {
                        "VEG0100": {
                            "id": "VEG0100",
                            "label": "Green Leafy Vegetables",
                            "include_subgroup": true,
                            "suppress_increase": false,
                            "suppress_decrease": true,
                            "value": 0.0
                        },
                        "VEG0200": {
                            "id": "VEG0200",
                            "label": "Deep-yellow Vegetables",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "VEG0300": {
                            "id": "VEG0300",
                            "label": "Tomato",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.2225255405208392
                        },
                        "VEG0400": {
                            "id": "VEG0400",
                            "label": "White Potatoes",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.2079278650626721
                        },
                        "VEG0450": {
                            "id": "VEG0450",
                            "label": "Other Starchy Vegetables",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "VEG0700": {
                            "id": "VEG0700",
                            "label": "Beans",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "VEG0600": {
                            "id": "VEG0600",
                            "label": "Other Vegetables",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.03097555524050081
                        },
                        "MSC0500": {
                            "id": "MSC0500",
                            "label": "Pickled Vegetables",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.08295752150616886
                        }
                    },
                    "label": "Vegetables",
                    "value": 0.5443864823301809
                },
                "old_value": 0.5443864823301809,
                "new_value": 14.42697332655556,
                "difference": 13.88258684422538,
                "children": [
                    {
                        "id": "VEG0700",
                        "label": "Beans",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "VEG0700",
                        "old_value": 0.0,
                        "new_value": 1.547838749611111,
                        "difference": 1.547838749611111
                    },
                    {
                        "id": "VEG0200",
                        "label": "Deep-yellow vegetables",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "VEG0200",
                        "old_value": 0.0,
                        "new_value": 0.6890001111111111,
                        "difference": 0.6890001111111111
                    },
                    {
                        "id": "VEG0100",
                        "label": "Green leafy vegetables",
                        "include_subgroup": true,
                        "suppress_increase": false,
                        "suppress_decrease": true,
                        "value": 0.0,
                        "key": "VEG0100",
                        "old_value": 0.0,
                        "new_value": 1.735246779833334,
                        "difference": 1.735246779833334
                    },
                    {
                        "id": "VEG0450",
                        "label": "Other starchy vegetables",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "VEG0450",
                        "old_value": 0.0,
                        "new_value": 0.8970781446666668,
                        "difference": 0.8970781446666668
                    },
                    {
                        "id": "VEG0600",
                        "label": "Other vegetables",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.03097555524050081,
                        "key": "VEG0600",
                        "old_value": 0.02997150483333334,
                        "new_value": 3.8229171165,
                        "difference": 3.792945611666667
                    },
                    {
                        "id": "MSC0500",
                        "label": "Pickled vegetables",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.08295752150616886,
                        "key": "MSC0500",
                        "old_value": 0.08026851294444447,
                        "new_value": 0.0,
                        "difference": -0.08026851294444447
                    },
                    {
                        "id": "VEG0300",
                        "label": "Tomato",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.2225255405208392,
                        "key": "VEG0300",
                        "old_value": 0.2153125347222223,
                        "new_value": 5.734892424833333,
                        "difference": 5.519579890111111
                    },
                    {
                        "id": "VEG0400",
                        "label": "White potatoes",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.2079278650626721,
                        "key": "VEG0400",
                        "old_value": 0.2011880324444444,
                        "new_value": 0.0,
                        "difference": -0.2011880324444444
                    }
                ]
            },
            {
                "key": "meta-fried-vegetables",
                "label": "Fried vegetables",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": true,
                    "suppress_decrease": false,
                    "children": {
                        "VEG0800": {
                            "id": "VEG0800",
                            "label": "French Fries",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.38131976623651
                        },
                        "VEG0900": {
                            "id": "VEG0900",
                            "label": "Fried Vegetables",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Fried Vegetables",
                    "value": 0.38131976623651
                },
                "old_value": 0.38131976623651,
                "new_value": 0.0,
                "difference": -0.38131976623651,
                "children": [
                    {
                        "id": "VEG0800",
                        "label": "French fries",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.38131976623651,
                        "key": "VEG0800",
                        "old_value": 0.3689595595,
                        "new_value": 0.0,
                        "difference": -0.3689595595
                    },
                    {
                        "id": "VEG0900",
                        "label": "Fried vegetables",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "VEG0900",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    }
                ]
            },
            {
                "key": "meta-beans-lentils",
                "label": "Beans & lentils",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": false,
                    "suppress_decrease": true,
                    "children": {
                        "VEG0700": {
                            "id": "VEG0700",
                            "label": "Beans",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Beans & Lentils",
                    "value": 0.0
                },
                "old_value": 0.0,
                "new_value": 1.547838749611111,
                "difference": 1.547838749611111,
                "children": [
                    {
                        "id": "VEG0700",
                        "label": "Beans",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "VEG0700",
                        "old_value": 0.0,
                        "new_value": 1.547838749611111,
                        "difference": 1.547838749611111
                    }
                ]
            },
            {
                "key": "meta-nuts-seeds",
                "label": "Nuts & seeds",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": false,
                    "suppress_decrease": true,
                    "children": {
                        "MOF0500": {
                            "id": "MOF0500",
                            "label": "Nuts and Seeds",
                            "include_subgroup": true,
                            "suppress_increase": false,
                            "suppress_decrease": true,
                            "value": 0.0
                        },
                        "MOF0600": {
                            "id": "MOF0600",
                            "label": "Nut and Seed Butters",
                            "include_subgroup": true,
                            "suppress_increase": false,
                            "suppress_decrease": true,
                            "value": 0.0
                        }
                    },
                    "label": "Nuts & Seeds",
                    "value": 0.0
                },
                "old_value": 0.0,
                "new_value": 2.151402846944444,
                "difference": 2.151402846944444,
                "children": [
                    {
                        "id": "MOF0600",
                        "label": "Nut and seed butters",
                        "include_subgroup": true,
                        "suppress_increase": false,
                        "suppress_decrease": true,
                        "value": 0.0,
                        "key": "MOF0600",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "MOF0500",
                        "label": "Nuts and seeds",
                        "include_subgroup": true,
                        "suppress_increase": false,
                        "suppress_decrease": true,
                        "value": 0.0,
                        "key": "MOF0500",
                        "old_value": 0.0,
                        "new_value": 2.151402846944444,
                        "difference": 2.151402846944444
                    }
                ]
            },
            {
                "key": "meta-whole-grains",
                "label": "Whole grains",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": false,
                    "suppress_decrease": true,
                    "children": {
                        "GRW0100": {
                            "id": "GRW0100",
                            "label": "Grains, Flour and Dry Mixes - Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRW0200": {
                            "id": "GRW0200",
                            "label": "Loaf-type Bread and Plain Rolls - Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRW0300": {
                            "id": "GRW0300",
                            "label": "Other Breads (quick breads, corn muffins, tortillas) - Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRW0400": {
                            "id": "GRW0400",
                            "label": "Crackers - Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRW0500": {
                            "id": "GRW0500",
                            "label": "Pasta - Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRW0600": {
                            "id": "GRW0600",
                            "label": "Ready-to-eat Cereal (not presweetened) - Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRW1100": {
                            "id": "GRW1100",
                            "label": "Popcorn",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRW0700": {
                            "id": "GRW0700",
                            "label": "Ready-to-eat Cereal (presweetened) - Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRW0900": {
                            "id": "GRW0900",
                            "label": "Snack Chips - Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRW1000": {
                            "id": "GRW1000",
                            "label": "Snack Bars - Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Whole Grains",
                    "value": 0.0
                },
                "old_value": 0.0,
                "new_value": 7.290999175777777,
                "difference": 7.290999175777777,
                "children": [
                    {
                        "id": "GRW0400",
                        "label": "Crackers - whole grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRW0400",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRW0100",
                        "label": "Grains, flour and dry mixes - whole grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRW0100",
                        "old_value": 0.0,
                        "new_value": 2.544477410333334,
                        "difference": 2.544477410333334
                    },
                    {
                        "id": "GRW0200",
                        "label": "Loaf-type bread and plain rolls - whole grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRW0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRW0500",
                        "label": "Pasta - whole grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRW0500",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRW1100",
                        "label": "Plain popcorn",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRW1100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRW0300",
                        "label": "Quick and other breads",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRW0300",
                        "old_value": 0.0,
                        "new_value": 4.746521765444444,
                        "difference": 4.746521765444444
                    },
                    {
                        "id": "GRW0600",
                        "label": "Ready-to-eat cereal (not presweetened) - whole grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRW0600",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRW0700",
                        "label": "Ready-to-eat cereal (presweetened) - whole grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRW0700",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRW1000",
                        "label": "Whole grain snack bars",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRW1000",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRW0900",
                        "label": "Whole grain snack chips",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRW0900",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    }
                ]
            },
            {
                "key": "meta-refined-grains",
                "label": "Refined grains",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": true,
                    "suppress_decrease": false,
                    "children": {
                        "GRS0100": {
                            "id": "GRS0100",
                            "label": "Grains, Flour and Dry Mixes - Some Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRR0100": {
                            "id": "GRR0100",
                            "label": "Grains, Flour and Dry Mixes - Refined Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 1.256468211996866
                        },
                        "GRS0200": {
                            "id": "GRS0200",
                            "label": "Loaf-type Bread and Plain Rolls - Some Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRR0200": {
                            "id": "GRR0200",
                            "label": "Loaf-type Bread and Plain Rolls - Refined Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 2.500474993724565
                        },
                        "GRS0300": {
                            "id": "GRS0300",
                            "label": "Other Breads (quick breads, corn muffins, tortillas) - Some Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRR0300": {
                            "id": "GRR0300",
                            "label": "Other Breads (quick breads, corn muffins, tortillas) - Refined Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRS0400": {
                            "id": "GRS0400",
                            "label": "Crackers - Some Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRR0400": {
                            "id": "GRR0400",
                            "label": "Crackers - Refined Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRS0500": {
                            "id": "GRS0500",
                            "label": "Pasta - Some Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRR0500": {
                            "id": "GRR0500",
                            "label": "Pasta - Refined Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.4069547085045107
                        },
                        "GRS0600": {
                            "id": "GRS0600",
                            "label": "Ready-to-eat Cereal (not presweetened) - Some Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRR0600": {
                            "id": "GRR0600",
                            "label": "Ready-to-eat Cereal (not presweetened) - Refined Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRS0700": {
                            "id": "GRS0700",
                            "label": "Ready-to-eat Cereal (presweetened) - Some Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRR0700": {
                            "id": "GRR0700",
                            "label": "Ready-to-eat Cereal (presweetened) - Refined Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRS0800": {
                            "id": "GRS0800",
                            "label": "Cakes, Cookies, Pies, Pastries, Danish, Doughnuts and Cobblers - Some Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRR0800": {
                            "id": "GRR0800",
                            "label": "Cakes, Cookies, Pies, Pastries, Danish, Doughnuts and Cobblers - Refined Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 2.598386231553735
                        },
                        "GRS1000": {
                            "id": "GRS1000",
                            "label": "Snack Bars - Some Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRR1000": {
                            "id": "GRR1000",
                            "label": "Snack Bars - Refined Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRS0900": {
                            "id": "GRS0900",
                            "label": "Snack Chips - Some Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRR0900": {
                            "id": "GRR0900",
                            "label": "Snack Chips - Refined Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Refined Grains",
                    "value": 6.762284145779677
                },
                "old_value": 6.762284145779677,
                "new_value": 0.0,
                "difference": -6.762284145779677,
                "children": [
                    {
                        "id": "GRR0800",
                        "label": "Cookies, pies, pastries, danish, etc.",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 2.598386231553735,
                        "key": "GRR0800",
                        "old_value": 2.514161405444445,
                        "new_value": 0.0,
                        "difference": -2.514161405444445
                    },
                    {
                        "id": "GRR0400",
                        "label": "Crackers - refined grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRR0400",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRS0400",
                        "label": "Crackers - some whole grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRS0400",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRR0100",
                        "label": "Grains, flour and dry mixes - refined grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 1.256468211996866,
                        "key": "GRR0100",
                        "old_value": 1.215740696055556,
                        "new_value": 0.0,
                        "difference": -1.215740696055556
                    },
                    {
                        "id": "GRS0100",
                        "label": "Grains, flour and dry mixes - some whole grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRS0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRR0200",
                        "label": "Loaf-type bread and plain rolls - refined grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 2.500474993724565,
                        "key": "GRR0200",
                        "old_value": 2.419423890166667,
                        "new_value": 0.0,
                        "difference": -2.419423890166667
                    },
                    {
                        "id": "GRS0200",
                        "label": "Loaf-type bread and plain rolls - some whole grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRS0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRS0300",
                        "label": "Other breads (quick breads, corn muffins, tortillas) - some whole grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRS0300",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRR0500",
                        "label": "Pasta - refined grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.4069547085045107,
                        "key": "GRR0500",
                        "old_value": 0.3937635635,
                        "new_value": 0.0,
                        "difference": -0.3937635635
                    },
                    {
                        "id": "GRS0500",
                        "label": "Pasta - some whole grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRS0500",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRR0600",
                        "label": "Ready-to-eat cereal (not presweetened) - refined grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRR0600",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRS0600",
                        "label": "Ready-to-eat cereal (not presweetened) - some whole grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRS0600",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRR0700",
                        "label": "Ready-to-eat cereal (presweetened) - refined grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRR0700",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRS0700",
                        "label": "Ready-to-eat cereal (presweetened) - some whole grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRS0700",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRR0300",
                        "label": "Refined snack breads",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRR0300",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRR1000",
                        "label": "Snack bars",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRR1000",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRS1000",
                        "label": "Snack bars some whole grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRS1000",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRR0900",
                        "label": "Snack chips",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRR0900",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRS0900",
                        "label": "Snack chips some whole grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRS0900",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRS0800",
                        "label": "Some whole grain cookies, pies, pastries, danish, etc.",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRS0800",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    }
                ]
            },
            {
                "key": "meta-full-fat-dairy-products",
                "label": "Full-fat dairy products",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": true,
                    "suppress_decrease": false,
                    "children": {
                        "DMF0100": {
                            "id": "DMF0100",
                            "label": "Whole Milk",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "DMF0200": {
                            "id": "DMF0200",
                            "label": "Ready-to-drink Flavored Milk - Whole",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "DCF0100": {
                            "id": "DCF0100",
                            "label": "Cheese",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 0.1758841872276713
                        },
                        "DYF0100": {
                            "id": "DYF0100",
                            "label": "Yogurt - Sweetened Whole Milk",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "DYF0200": {
                            "id": "DYF0200",
                            "label": "Yogurt - Artificially Sweetened Whole Milk",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "FCF0100": {
                            "id": "FCF0100",
                            "label": "Cream",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 0.2588417087338401
                        }
                    },
                    "label": "Full-Fat Dairy Products",
                    "value": 0.4347258959615114
                },
                "old_value": 0.4347258959615114,
                "new_value": 0.3913520631111111,
                "difference": -0.04337383285040025,
                "children": [
                    {
                        "id": "DCF0100",
                        "label": "Cheese",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.1758841872276713,
                        "key": "DCF0100",
                        "old_value": 0.1701830274444444,
                        "new_value": 0.2676765431666667,
                        "difference": 0.09749351572222228
                    },
                    {
                        "id": "FCF0100",
                        "label": "Cream",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.2588417087338401,
                        "key": "FCF0100",
                        "old_value": 0.2504515403888889,
                        "new_value": 0.1236755199444445,
                        "difference": -0.1267760204444444
                    },
                    {
                        "id": "DMF0200",
                        "label": "Ready-to-drink flavored milk - whole",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DMF0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "DMF0100",
                        "label": "Whole milk",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DMF0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "DYF0200",
                        "label": "Yogurt - artificially sweetened whole milk",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DYF0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "DYF0100",
                        "label": "Yogurt - sweetened whole milk",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DYF0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    }
                ]
            },
            {
                "key": "meta-reduced-or-non-fat-dairy-products",
                "label": "Reduced or non-fat dairy products",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": false,
                    "suppress_decrease": true,
                    "children": {
                        "DMR0100": {
                            "id": "DMR0100",
                            "label": "Milk - Reduced Fat",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "DMR0200": {
                            "id": "DMR0200",
                            "label": "Ready-to-drink Flavored Milk - Reduced Fat",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "DCR0100": {
                            "id": "DCR0100",
                            "label": "Cheese - Reduced Fat",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.3852362157496768
                        },
                        "DYR0100": {
                            "id": "DYR0100",
                            "label": "Yogurt - Sweetened Low Fat",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "DYR0200": {
                            "id": "DYR0200",
                            "label": "Yogurt - Artificially Sweetened Low Fat",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "FCR0100": {
                            "id": "FCR0100",
                            "label": "Cream - Reduced Fat",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "DML0100": {
                            "id": "DML0100",
                            "label": "Milk - Low Fat and Fat Free",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "DML0200": {
                            "id": "DML0200",
                            "label": "Ready-to-drink Flavored Milk - Low Fat and Fat Free",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "DCL0100": {
                            "id": "DCL0100",
                            "label": "Cheese - Low Fat and Fat Free",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "DYL0100": {
                            "id": "DYL0100",
                            "label": "Yogurt - Sweetened Fat Free",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "DYL0200": {
                            "id": "DYL0200",
                            "label": "Yogurt - Artificially Sweetened Fat Free",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "FCL0100": {
                            "id": "FCL0100",
                            "label": "Cream - Low Fat and Fat Free",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Reduced or Non-Fat Dairy Products",
                    "value": 0.3852362157496768
                },
                "old_value": 0.3852362157496768,
                "new_value": 2.112818840722222,
                "difference": 1.727582624972546,
                "children": [
                    {
                        "id": "DCL0100",
                        "label": "Cheese - low fat and fat free",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DCL0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "DCR0100",
                        "label": "Cheese - reduced fat",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.3852362157496768,
                        "key": "DCR0100",
                        "old_value": 0.3727490601111112,
                        "new_value": 1.033155666611111,
                        "difference": 0.6604066065
                    },
                    {
                        "id": "FCL0100",
                        "label": "Cream - low fat and fat free",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "FCL0100",
                        "old_value": 0.0,
                        "new_value": 0.3300310532222222,
                        "difference": 0.3300310532222222
                    },
                    {
                        "id": "FCR0100",
                        "label": "Cream - reduced fat",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "FCR0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "DML0100",
                        "label": "Milk - low fat and fat free",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DML0100",
                        "old_value": 0.0,
                        "new_value": 0.4306250694444445,
                        "difference": 0.4306250694444445
                    },
                    {
                        "id": "DMR0100",
                        "label": "Milk - reduced fat",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DMR0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "DML0200",
                        "label": "Ready-to-drink flavored milk - low fat and fat free",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DML0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "DMR0200",
                        "label": "Ready-to-drink flavored milk - reduced fat",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DMR0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "DYL0200",
                        "label": "Yogurt - artificially sweetened fat free",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DYL0200",
                        "old_value": 0.0,
                        "new_value": 0.3190070514444445,
                        "difference": 0.3190070514444445
                    },
                    {
                        "id": "DYR0200",
                        "label": "Yogurt - artificially sweetened low fat",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DYR0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "DYL0100",
                        "label": "Yogurt - sweetened fat free",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DYL0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "DYR0100",
                        "label": "Yogurt - sweetened low fat",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DYR0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    }
                ]
            },
            {
                "key": "meta-plant-based-dairy-alternatives",
                "label": "Plant-based dairy alternatives",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": false,
                    "suppress_decrease": true,
                    "children": {
                        "DMN0100": {
                            "id": "DMN0100",
                            "label": "Milk - Nondairy",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.006764776431833511
                        },
                        "DCN0100": {
                            "id": "DCN0100",
                            "label": "Cheese - Nondairy",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "DYN0100": {
                            "id": "DYN0100",
                            "label": "Yogurt - Nondairy",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "DOT0200": {
                            "id": "DOT0200",
                            "label": "Frozen Nondairy Dessert",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.3111797158643415
                        },
                        "FCN0100": {
                            "id": "FCN0100",
                            "label": "Cream - Nondairy, Soy, rice, grain based",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 2.848326918666741
                        }
                    },
                    "label": "Plant-Based Dairy Alternatives",
                    "value": 3.166271410962916
                },
                "old_value": 3.166271410962916,
                "new_value": 0.0,
                "difference": -3.166271410962916,
                "children": [
                    {
                        "id": "DCN0100",
                        "label": "Cheese - nondairy",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DCN0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "FCN0100",
                        "label": "Cream - nondairy, soy, rice, grain based",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 2.848326918666741,
                        "key": "FCN0100",
                        "old_value": 2.756000444444445,
                        "new_value": 0.0,
                        "difference": -2.756000444444445
                    },
                    {
                        "id": "DOT0200",
                        "label": "Frozen nondairy dessert",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.3111797158643415,
                        "key": "DOT0200",
                        "old_value": 0.3010930485555556,
                        "new_value": 0.0,
                        "difference": -0.3010930485555556
                    },
                    {
                        "id": "DMN0100",
                        "label": "Milk - nondairy",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.006764776431833511,
                        "key": "DMN0100",
                        "old_value": 0.006545501055555556,
                        "new_value": 0.0,
                        "difference": -0.006545501055555556
                    },
                    {
                        "id": "DYN0100",
                        "label": "Yogurt - nondairy",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DYN0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    }
                ]
            },
            {
                "key": "meta-processed-meat",
                "label": "Processed meat",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": true,
                    "suppress_decrease": false,
                    "children": {
                        "MCF0100": {
                            "id": "MCF0100",
                            "label": "Cold Cuts and Sausage",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 1.840375230323548
                        },
                        "MCL0100": {
                            "id": "MCL0100",
                            "label": "Lean Cold Cuts and Sausage",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.07120817296666854
                        },
                        "MCF0200": {
                            "id": "MCF0200",
                            "label": "Cured Pork",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.2720152207326738
                        },
                        "MCL0200": {
                            "id": "MCL0200",
                            "label": "Lean Cured Pork",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "FMC0200": {
                            "id": "FMC0200",
                            "label": "Meat-based Savory Snack",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Processed Meat",
                    "value": 2.183598624022891
                },
                "old_value": 2.183598624022891,
                "new_value": 0.0,
                "difference": -2.183598624022891,
                "children": [
                    {
                        "id": "MCF0100",
                        "label": "Cold cuts and sausage",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 1.840375230323548,
                        "key": "MCF0100",
                        "old_value": 1.780720787166667,
                        "new_value": 0.0,
                        "difference": -1.780720787166667
                    },
                    {
                        "id": "MCF0200",
                        "label": "Cured pork",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.2720152207326738,
                        "key": "MCF0200",
                        "old_value": 0.2631980424444444,
                        "new_value": 0.0,
                        "difference": -0.2631980424444444
                    },
                    {
                        "id": "MCL0100",
                        "label": "Lean cold cuts and sausage",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.07120817296666854,
                        "key": "MCL0100",
                        "old_value": 0.06890001111111112,
                        "new_value": 0.0,
                        "difference": -0.06890001111111112
                    },
                    {
                        "id": "MCL0200",
                        "label": "Lean cured pork",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MCL0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "FMC0200",
                        "label": "Meat based savory snack (pork rinds)",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "FMC0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    }
                ]
            },
            {
                "key": "meta-red-meat",
                "label": "Red meat",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": true,
                    "suppress_decrease": false,
                    "children": {
                        "MRF0100": {
                            "id": "MRF0100",
                            "label": "Beef",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 1.042131611367194
                        },
                        "MRL0100": {
                            "id": "MRL0100",
                            "label": "Lean Beef",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "MRF0200": {
                            "id": "MRF0200",
                            "label": "Veal",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "MRL0200": {
                            "id": "MRL0200",
                            "label": "Lean Veal",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "MRF0300": {
                            "id": "MRF0300",
                            "label": "Lamb",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "MRL0300": {
                            "id": "MRL0300",
                            "label": "Lean Lamb",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "MRF0400": {
                            "id": "MRF0400",
                            "label": "Fresh Pork",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "MRL0400": {
                            "id": "MRL0400",
                            "label": "Lean Fresh Pork",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "MRF0500": {
                            "id": "MRF0500",
                            "label": "Game",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "MOF0100": {
                            "id": "MOF0100",
                            "label": "Organ Meats",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Red Meat",
                    "value": 1.042131611367194
                },
                "old_value": 1.042131611367194,
                "new_value": 0.0,
                "difference": -1.042131611367194,
                "children": [
                    {
                        "id": "MRF0100",
                        "label": "Beef",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 1.042131611367194,
                        "key": "MRF0100",
                        "old_value": 1.008351662611111,
                        "new_value": 0.0,
                        "difference": -1.008351662611111
                    },
                    {
                        "id": "MRF0400",
                        "label": "Fresh pork",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MRF0400",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "MRF0500",
                        "label": "Game",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MRF0500",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "MRF0300",
                        "label": "Lamb",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MRF0300",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "MRL0100",
                        "label": "Lean beef",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MRL0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "MRL0400",
                        "label": "Lean fresh pork",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MRL0400",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "MRL0300",
                        "label": "Lean lamb",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MRL0300",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "MRL0200",
                        "label": "Lean veal",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MRL0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "MOF0100",
                        "label": "Organ meats",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MOF0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "MRF0200",
                        "label": "Veal",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MRF0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    }
                ]
            },
            {
                "key": "meta-poultry",
                "label": "Poultry",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": false,
                    "suppress_decrease": false,
                    "children": {
                        "MPF0100": {
                            "id": "MPF0100",
                            "label": "Poultry",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "MPL0100": {
                            "id": "MPL0100",
                            "label": "Lean Poultry",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "MPF0200": {
                            "id": "MPF0200",
                            "label": "Fried Chicken",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 1.356515695015036
                        }
                    },
                    "label": "Poultry",
                    "value": 1.356515695015036
                },
                "old_value": 1.356515695015036,
                "new_value": 1.276028205777778,
                "difference": -0.08048748923725757,
                "children": [
                    {
                        "id": "MPF0200",
                        "label": "Fried chicken",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 1.356515695015036,
                        "key": "MPF0200",
                        "old_value": 1.312545211666667,
                        "new_value": 0.0,
                        "difference": -1.312545211666667
                    },
                    {
                        "id": "MPL0100",
                        "label": "Lean poultry",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MPL0100",
                        "old_value": 0.0,
                        "new_value": 1.276028205777778,
                        "difference": 1.276028205777778
                    },
                    {
                        "id": "MPF0100",
                        "label": "Poultry",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MPF0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    }
                ]
            },
            {
                "key": "meta-fish",
                "label": "Fish",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": false,
                    "suppress_decrease": false,
                    "children": {
                        "MFF0100": {
                            "id": "MFF0100",
                            "label": "Fish",
                            "include_subgroup": true,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "MFL0100": {
                            "id": "MFL0100",
                            "label": "Lean Fish",
                            "include_subgroup": true,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "MFF0200": {
                            "id": "MFF0200",
                            "label": "Fried Fish",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Fish",
                    "value": 0.0
                },
                "old_value": 0.0,
                "new_value": 2.149680346666667,
                "difference": 2.149680346666667,
                "children": [
                    {
                        "id": "MFF0100",
                        "label": "Fish",
                        "include_subgroup": true,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MFF0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "MFF0200",
                        "label": "Fried fish",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MFF0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "MFL0100",
                        "label": "Lean fish",
                        "include_subgroup": true,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MFL0100",
                        "old_value": 0.0,
                        "new_value": 2.149680346666667,
                        "difference": 2.149680346666667
                    }
                ]
            },
            {
                "key": "meta-eggs",
                "label": "Eggs",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": true,
                    "suppress_decrease": false,
                    "children": {
                        "MOF0300": {
                            "id": "MOF0300",
                            "label": "Eggs",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "MOF0400": {
                            "id": "MOF0400",
                            "label": "Egg Substitute",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Eggs",
                    "value": 0.0
                },
                "old_value": 0.0,
                "new_value": 0.6890001111111111,
                "difference": 0.6890001111111111,
                "children": [
                    {
                        "id": "MOF0400",
                        "label": "Egg substitute",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MOF0400",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "MOF0300",
                        "label": "Eggs",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MOF0300",
                        "old_value": 0.0,
                        "new_value": 0.6890001111111111,
                        "difference": 0.6890001111111111
                    }
                ]
            },
            {
                "key": "meta-plant-based-meat-alternatives",
                "label": "Plant-based meat alternatives",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": false,
                    "suppress_decrease": true,
                    "children": {
                        "MOF0700": {
                            "id": "MOF0700",
                            "label": "Meat Alternatives",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.006408735567000168
                        }
                    },
                    "label": "Plant-Based Meat Alternatives",
                    "value": 0.006408735567000168
                },
                "old_value": 0.006408735567000168,
                "new_value": 0.0,
                "difference": -0.006408735567000168,
                "children": [
                    {
                        "id": "MOF0700",
                        "label": "Meat alternatives",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.006408735567000168,
                        "key": "MOF0700",
                        "old_value": 0.006201001,
                        "new_value": 0.0,
                        "difference": -0.006201001
                    }
                ]
            },
            {
                "key": "meta-saturated-unhealthy-fats",
                "label": "Saturated (unhealthy) fats",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": true,
                    "suppress_decrease": false,
                    "children": {
                        "FMF0100": {
                            "id": "FMF0100",
                            "label": "Margarine",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "FMR0100": {
                            "id": "FMR0100",
                            "label": "Margarine - Reduced Fat",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "FSF0100": {
                            "id": "FSF0100",
                            "label": "Shortening",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 0.1940422713341718
                        },
                        "FAF0100": {
                            "id": "FAF0100",
                            "label": "Butter",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 1.780204324166713
                        },
                        "FAR0100": {
                            "id": "FAR0100",
                            "label": "Butter and Other Animal Fats - Reduced Fat",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "FDF0100": {
                            "id": "FDF0100",
                            "label": "Creamy Dressings",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "FCF0100": {
                            "id": "FCF0100",
                            "label": "Cream",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 0.2588417087338401
                        }
                    },
                    "label": "Saturated (Unhealthy) Fats",
                    "value": 2.233088304234725
                },
                "old_value": 2.233088304234725,
                "new_value": 0.1236755199444445,
                "difference": -2.109412784290281,
                "children": [
                    {
                        "id": "FAF0100",
                        "label": "Butter",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 1.780204324166713,
                        "key": "FAF0100",
                        "old_value": 1.722500277777778,
                        "new_value": 0.0,
                        "difference": -1.722500277777778
                    },
                    {
                        "id": "FAR0100",
                        "label": "Butter and other animal fats - reduced fat",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "FAR0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "FCF0100",
                        "label": "Cream",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.2588417087338401,
                        "key": "FCF0100",
                        "old_value": 0.2504515403888889,
                        "new_value": 0.1236755199444445,
                        "difference": -0.1267760204444444
                    },
                    {
                        "id": "FDF0100",
                        "label": "Creamy dressings",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "FDF0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "FMF0100",
                        "label": "Margarine",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "FMF0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "FMR0100",
                        "label": "Margarine - reduced fat",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "FMR0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "FSF0100",
                        "label": "Shortening",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.1940422713341718,
                        "key": "FSF0100",
                        "old_value": 0.1877525302777778,
                        "new_value": 0.0,
                        "difference": -0.1877525302777778
                    }
                ]
            },
            {
                "key": "meta-unsaturated-healthier-fats",
                "label": "Unsaturated (healthier) fats",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": false,
                    "suppress_decrease": true,
                    "children": {
                        "FOF0100": {
                            "id": "FOF0100",
                            "label": "Vegetable Oils",
                            "include_subgroup": true,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 1.023261445531027
                        },
                        "FRU0500": {
                            "id": "FRU0500",
                            "label": "Avocados",
                            "include_subgroup": true,
                            "suppress_increase": false,
                            "suppress_decrease": true,
                            "value": 0.0
                        }
                    },
                    "label": "Unsaturated (Healthier) Fats",
                    "value": 1.023261445531027
                },
                "old_value": 1.023261445531027,
                "new_value": 5.373167366500001,
                "difference": 4.349905920968974,
                "children": [
                    {
                        "id": "FRU0500",
                        "label": "Avocados",
                        "include_subgroup": true,
                        "suppress_increase": false,
                        "suppress_decrease": true,
                        "value": 0.0,
                        "key": "FRU0500",
                        "old_value": 0.0,
                        "new_value": 0.824044132888889,
                        "difference": 0.824044132888889
                    },
                    {
                        "id": "FOF0100",
                        "label": "Vegetable oils",
                        "include_subgroup": true,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 1.023261445531027,
                        "key": "FOF0100",
                        "old_value": 0.9900931596666669,
                        "new_value": 4.549123233611112,
                        "difference": 3.559030073944445
                    }
                ]
            },
            {
                "key": "meta-sweetened-beverages",
                "label": "Sweetened beverages",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": true,
                    "suppress_decrease": false,
                    "children": {
                        "BVS0400": {
                            "id": "BVS0400",
                            "label": "Soda",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 2.67030648625007
                        },
                        "BVS0300": {
                            "id": "BVS0300",
                            "label": "Sweetened Fruit Drinks",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "BVS0500": {
                            "id": "BVS0500",
                            "label": "Sweetened Tea",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "BVS0100": {
                            "id": "BVS0100",
                            "label": "Sweetened Coffee",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "BVS0200": {
                            "id": "BVS0200",
                            "label": "Sweetened Coffee Substitutes",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "BVS0600": {
                            "id": "BVS0600",
                            "label": "Sweetened Water",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "BVO0100": {
                            "id": "BVO0100",
                            "label": "Non-alcoholic Beer",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "BVO0200": {
                            "id": "BVO0200",
                            "label": "Non-alcoholic Light Beer",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Sweetened Beverages",
                    "value": 2.67030648625007
                },
                "old_value": 2.67030648625007,
                "new_value": 0.0,
                "difference": -2.67030648625007,
                "children": [
                    {
                        "id": "BVO0100",
                        "label": "Non-alcoholic beer",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVO0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "BVO0200",
                        "label": "Non-alcoholic light beer",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVO0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "BVS0400",
                        "label": "Soda",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 2.67030648625007,
                        "key": "BVS0400",
                        "old_value": 2.583750416666667,
                        "new_value": 0.0,
                        "difference": -2.583750416666667
                    },
                    {
                        "id": "BVS0100",
                        "label": "Sweetened coffee",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVS0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "BVS0200",
                        "label": "Sweetened coffee substitutes",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVS0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "BVS0300",
                        "label": "Sweetened fruit drinks",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVS0300",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "BVS0500",
                        "label": "Sweetened tea",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVS0500",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "BVS0600",
                        "label": "Sweetened water",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVS0600",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    }
                ]
            },
            {
                "key": "meta-artificially-sweetened-beverages",
                "label": "Artificially sweetened beverages",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": true,
                    "suppress_decrease": false,
                    "children": {
                        "BVA0500": {
                            "id": "BVA0500",
                            "label": "Artificially Sweetened Tea",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "BVA0100": {
                            "id": "BVA0100",
                            "label": "Artificially Sweetened Coffee",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "BVA0200": {
                            "id": "BVA0200",
                            "label": "Artificially Sweetened Coffee Substitutes",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "BVA0600": {
                            "id": "BVA0600",
                            "label": "Artificially Sweetened Water",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "BVA0400": {
                            "id": "BVA0400",
                            "label": "Diet Soda",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 0.5340612972500141
                        },
                        "BVA0300": {
                            "id": "BVA0300",
                            "label": "Artificially Sweetened Fruit Drinks",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Artificially Sweetened Beverages",
                    "value": 0.5340612972500141
                },
                "old_value": 0.5340612972500141,
                "new_value": 0.0,
                "difference": -0.5340612972500141,
                "children": [
                    {
                        "id": "BVA0100",
                        "label": "Artificially sweetened coffee",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVA0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "BVA0200",
                        "label": "Artificially sweetened coffee substitutes",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVA0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "BVA0300",
                        "label": "Artificially sweetened fruit drinks",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVA0300",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "BVA0500",
                        "label": "Artificially sweetened tea",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVA0500",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "BVA0600",
                        "label": "Artificially sweetened water",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVA0600",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "BVA0400",
                        "label": "Diet soda",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.5340612972500141,
                        "key": "BVA0400",
                        "old_value": 0.5167500833333334,
                        "new_value": 0.0,
                        "difference": -0.5167500833333334
                    }
                ]
            },
            {
                "key": "meta-unsweetened-beverages",
                "label": "Unsweetened beverages",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": false,
                    "suppress_decrease": true,
                    "children": {
                        "BVU0500": {
                            "id": "BVU0500",
                            "label": "Water",
                            "include_subgroup": true,
                            "suppress_increase": false,
                            "suppress_decrease": true,
                            "value": 0.0
                        },
                        "BVU0300": {
                            "id": "BVU0300",
                            "label": "Unsweetened Soft Drinks",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "BVU0400": {
                            "id": "BVU0400",
                            "label": "Unsweetened Tea",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "BVU0100": {
                            "id": "BVU0100",
                            "label": "Unsweetened Coffee",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.6960598907491851
                        },
                        "BVU0200": {
                            "id": "BVU0200",
                            "label": "Unsweetened Coffee Substitutes",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Unsweetened Beverages",
                    "value": 0.6960598907491851
                },
                "old_value": 0.6960598907491851,
                "new_value": 2.325375375,
                "difference": 1.629315484250815,
                "children": [
                    {
                        "id": "BVU0100",
                        "label": "Unsweetened coffee",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.6960598907491851,
                        "key": "BVU0100",
                        "old_value": 0.6734976086111113,
                        "new_value": 0.0,
                        "difference": -0.6734976086111113
                    },
                    {
                        "id": "BVU0200",
                        "label": "Unsweetened coffee substitutes",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVU0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "BVU0300",
                        "label": "Unsweetened soft drinks",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVU0300",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "BVU0400",
                        "label": "Unsweetened tea",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVU0400",
                        "old_value": 0.0,
                        "new_value": 0.2583750416666667,
                        "difference": 0.2583750416666667
                    },
                    {
                        "id": "BVU0500",
                        "label": "Water",
                        "include_subgroup": true,
                        "suppress_increase": false,
                        "suppress_decrease": true,
                        "value": 0.0,
                        "key": "BVU0500",
                        "old_value": 0.0,
                        "new_value": 2.067000333333334,
                        "difference": 2.067000333333334
                    }
                ]
            },
            {
                "key": "meta-alcoholic-beverages",
                "label": "Alcoholic beverages",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": true,
                    "suppress_decrease": false,
                    "children": {
                        "BVE0100": {
                            "id": "BVE0100",
                            "label": "Beer and Ales",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.3560408648333426
                        },
                        "BVE0400": {
                            "id": "BVE0400",
                            "label": "Cordial and Liqueur",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "BVE0300": {
                            "id": "BVE0300",
                            "label": "Distilled Liquor",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "BVE0200": {
                            "id": "BVE0200",
                            "label": "Wine",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Alcoholic Beverages",
                    "value": 0.3560408648333426
                },
                "old_value": 0.3560408648333426,
                "new_value": 0.0,
                "difference": -0.3560408648333426,
                "children": [
                    {
                        "id": "BVE0100",
                        "label": "Beer and ales",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.3560408648333426,
                        "key": "BVE0100",
                        "old_value": 0.3445000555555556,
                        "new_value": 0.0,
                        "difference": -0.3445000555555556
                    },
                    {
                        "id": "BVE0400",
                        "label": "Cordial and liqueur",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVE0400",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "BVE0300",
                        "label": "Distilled liquor",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVE0300",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "BVE0200",
                        "label": "Wine",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "BVE0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    }
                ]
            },
            {
                "key": "meta-fried-foods",
                "label": "Fried foods",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": true,
                    "suppress_decrease": false,
                    "children": {
                        "MSF0100": {
                            "id": "MSF0100",
                            "label": "Fried Shellfish",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "MFF0200": {
                            "id": "MFF0200",
                            "label": "Fried Fish",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "FMC0100": {
                            "id": "FMC0100",
                            "label": "Vegetable-based Savory Snack",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "VEG0800": {
                            "id": "VEG0800",
                            "label": "French Fries",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 0.38131976623651
                        },
                        "VEG0900": {
                            "id": "VEG0900",
                            "label": "Fried Vegetables",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "FRU0600": {
                            "id": "FRU0600",
                            "label": "Fried Fruits",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "MPF0200": {
                            "id": "MPF0200",
                            "label": "Fried Chicken",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 1.356515695015036
                        }
                    },
                    "label": "Fried Foods",
                    "value": 1.737835461251546
                },
                "old_value": 1.737835461251546,
                "new_value": 0.0,
                "difference": -1.737835461251546,
                "children": [
                    {
                        "id": "VEG0800",
                        "label": "French fries",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.38131976623651,
                        "key": "VEG0800",
                        "old_value": 0.3689595595,
                        "new_value": 0.0,
                        "difference": -0.3689595595
                    },
                    {
                        "id": "MPF0200",
                        "label": "Fried chicken",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 1.356515695015036,
                        "key": "MPF0200",
                        "old_value": 1.312545211666667,
                        "new_value": 0.0,
                        "difference": -1.312545211666667
                    },
                    {
                        "id": "MFF0200",
                        "label": "Fried fish",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MFF0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "FRU0600",
                        "label": "Fried fruits",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "FRU0600",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "MSF0100",
                        "label": "Fried shellfish",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MSF0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "VEG0900",
                        "label": "Fried vegetables",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "VEG0900",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "FMC0100",
                        "label": "Vegetable based savory snack (potato chips)",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "FMC0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    }
                ]
            },
            {
                "key": "meta-sweets-desserts",
                "label": "Sweets & desserts",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": true,
                    "suppress_decrease": false,
                    "children": {
                        "MSC0600": {
                            "id": "MSC0600",
                            "label": "Misc",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "DOT0100": {
                            "id": "DOT0100",
                            "label": "Frozen Dairy Dessert",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "DOT0200": {
                            "id": "DOT0200",
                            "label": "Frozen Nondairy Dessert",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.3111797158643415
                        },
                        "DOT0300": {
                            "id": "DOT0300",
                            "label": "Pudding and Other Dairy Dessert",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "DOT0400": {
                            "id": "DOT0400",
                            "label": "Artificially Sweetened Pudding and Other Dairy Dessert",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRW0800": {
                            "id": "GRW0800",
                            "label": "Whole Grain Cookies, Pies, Pastries, Danish, etc.",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRS0800": {
                            "id": "GRS0800",
                            "label": "Some Whole Grain Cookies, Pies, Pastries, Danish, etc.",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRR0800": {
                            "id": "GRR0800",
                            "label": "Cookies, Pies, Pastries, Danish, etc.",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 2.598386231553735
                        },
                        "GRS1000": {
                            "id": "GRS1000",
                            "label": "Snack Bars Some Whole Grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRR1000": {
                            "id": "GRR1000",
                            "label": "Snack Bars",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRR0300": {
                            "id": "GRR0300",
                            "label": "Refined Snack Breads",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "SWT0500": {
                            "id": "SWT0500",
                            "label": "Syrup, Honey, Jam",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.1619985934991709
                        },
                        "SWT0400": {
                            "id": "SWT0400",
                            "label": "Sugar",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 3.797531864312433
                        },
                        "SWT0700": {
                            "id": "SWT0700",
                            "label": "Fudge, caramel, etc.",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "SWT0100": {
                            "id": "SWT0100",
                            "label": "Chocolate Candy",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.1424163459333371
                        },
                        "SWT0200": {
                            "id": "SWT0200",
                            "label": "Candy",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.7729647175531869
                        },
                        "SWT0300": {
                            "id": "SWT0300",
                            "label": "Frosting or icing",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Sweets & Desserts",
                    "value": 7.784477468716204
                },
                "old_value": 7.784477468716204,
                "new_value": 0.0,
                "difference": -7.784477468716204,
                "children": [
                    {
                        "id": "DOT0400",
                        "label": "Artificially sweetened pudding and other dairy dessert",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DOT0400",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "SWT0200",
                        "label": "Candy",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.7729647175531869,
                        "key": "SWT0200",
                        "old_value": 0.7479096206111111,
                        "new_value": 0.0,
                        "difference": -0.7479096206111111
                    },
                    {
                        "id": "SWT0100",
                        "label": "Chocolate candy",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.1424163459333371,
                        "key": "SWT0100",
                        "old_value": 0.1378000222222222,
                        "new_value": 0.0,
                        "difference": -0.1378000222222222
                    },
                    {
                        "id": "GRR0800",
                        "label": "Cookies, pies, pastries, danish, etc.",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 2.598386231553735,
                        "key": "GRR0800",
                        "old_value": 2.514161405444445,
                        "new_value": 0.0,
                        "difference": -2.514161405444445
                    },
                    {
                        "id": "SWT0300",
                        "label": "Frosting or icing",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "SWT0300",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "DOT0100",
                        "label": "Frozen dairy dessert",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DOT0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "DOT0200",
                        "label": "Frozen nondairy dessert",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.3111797158643415,
                        "key": "DOT0200",
                        "old_value": 0.3010930485555556,
                        "new_value": 0.0,
                        "difference": -0.3010930485555556
                    },
                    {
                        "id": "SWT0700",
                        "label": "Fudge, caramel, butterscotch, etc.",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "SWT0700",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "MSC0600",
                        "label": "Misc",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MSC0600",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "DOT0300",
                        "label": "Pudding and other dairy dessert",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "DOT0300",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRR0300",
                        "label": "Refined snack breads",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRR0300",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRR1000",
                        "label": "Snack bars",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRR1000",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRS1000",
                        "label": "Snack bars some whole grain",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRS1000",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRS0800",
                        "label": "Some whole grain cookies, pies, pastries, danish, etc.",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRS0800",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "SWT0400",
                        "label": "Sugar",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 3.797531864312433,
                        "key": "SWT0400",
                        "old_value": 3.674437592555556,
                        "new_value": 0.0,
                        "difference": -3.674437592555556
                    },
                    {
                        "id": "SWT0500",
                        "label": "Syrup, honey, jam",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.1619985934991709,
                        "key": "SWT0500",
                        "old_value": 0.1567475252777778,
                        "new_value": 0.0,
                        "difference": -0.1567475252777778
                    },
                    {
                        "id": "GRW0800",
                        "label": "Whole grain cookies, pies, pastries, danish, etc.",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRW0800",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    }
                ]
            },
            {
                "key": "meta-healthier-snacks",
                "label": "Healthier snacks",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": false,
                    "suppress_decrease": true,
                    "children": {
                        "GRW1000": {
                            "id": "GRW1000",
                            "label": "Whole Grain Snack Bars",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRW0900": {
                            "id": "GRW0900",
                            "label": "Whole Grain Snack Chips",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRW1100": {
                            "id": "GRW1100",
                            "label": "Plain Popcorn",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "GRW0300": {
                            "id": "GRW0300",
                            "label": "Quick and Other Breads",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Healthier Snacks",
                    "value": 0.0
                },
                "old_value": 0.0,
                "new_value": 4.746521765444444,
                "difference": 4.746521765444444,
                "children": [
                    {
                        "id": "GRW1100",
                        "label": "Plain popcorn",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRW1100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRW0300",
                        "label": "Quick and other breads",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRW0300",
                        "old_value": 0.0,
                        "new_value": 4.746521765444444,
                        "difference": 4.746521765444444
                    },
                    {
                        "id": "GRW1000",
                        "label": "Whole grain snack bars",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRW1000",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "GRW0900",
                        "label": "Whole grain snack chips",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "GRW0900",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    }
                ]
            },
            {
                "key": "meta-sweet-salty-condiments",
                "label": "Sweet / salty condiments",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": true,
                    "suppress_decrease": false,
                    "children": {
                        "MSC0400": {
                            "id": "MSC0400",
                            "label": "Sweet/Salty Sauces",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 3.306907552572087
                        },
                        "SWT0500": {
                            "id": "SWT0500",
                            "label": "Syrup, Honey, Jam",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.1619985934991709
                        },
                        "SWT0400": {
                            "id": "SWT0400",
                            "label": "Sugar",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 3.797531864312433
                        },
                        "SWT0700": {
                            "id": "SWT0700",
                            "label": "Fudge, caramel, butterscotch, etc.",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "SWT0300": {
                            "id": "SWT0300",
                            "label": "Frosting or icing",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Sweet / Salty Condiments",
                    "value": 7.266438010383692
                },
                "old_value": 7.266438010383692,
                "new_value": 0.4592185740555556,
                "difference": -6.807219436328136,
                "children": [
                    {
                        "id": "SWT0300",
                        "label": "Frosting or icing",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "SWT0300",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "SWT0700",
                        "label": "Fudge, caramel, butterscotch, etc.",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "SWT0700",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "SWT0400",
                        "label": "Sugar",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 3.797531864312433,
                        "key": "SWT0400",
                        "old_value": 3.674437592555556,
                        "new_value": 0.0,
                        "difference": -3.674437592555556
                    },
                    {
                        "id": "MSC0400",
                        "label": "Sweet/salty sauces",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 3.306907552572087,
                        "key": "MSC0400",
                        "old_value": 3.1997165160000005,
                        "new_value": 0.4592185740555556,
                        "difference": -2.740497941944445
                    },
                    {
                        "id": "SWT0500",
                        "label": "Syrup, honey, jam",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.1619985934991709,
                        "key": "SWT0500",
                        "old_value": 0.1567475252777778,
                        "new_value": 0.0,
                        "difference": -0.1567475252777778
                    }
                ]
            },
            {
                "key": "meta-fatty-condiments",
                "label": "Fatty condiments",
                "parent_info": {
                    "include_subgroup": false,
                    "suppress_increase": true,
                    "suppress_decrease": false,
                    "children": {
                        "MSC0100": {
                            "id": "MSC0100",
                            "label": "Gravy",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 0.1780204324166713
                        },
                        "MSC0200": {
                            "id": "MSC0200",
                            "label": "Reduced Fat Gravy",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "MSC0300": {
                            "id": "MSC0300",
                            "label": "Fatty Sauces",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0
                        },
                        "FCF0100": {
                            "id": "FCF0100",
                            "label": "Cream",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 0.2588417087338401
                        },
                        "FDF0100": {
                            "id": "FDF0100",
                            "label": "Creamy Dressings",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 0.0
                        }
                    },
                    "label": "Fatty Condiments",
                    "value": 0.4368621411505115
                },
                "old_value": 0.4368621411505115,
                "new_value": 0.1236755199444445,
                "difference": -0.313186621206067,
                "children": [
                    {
                        "id": "FCF0100",
                        "label": "Cream",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.2588417087338401,
                        "key": "FCF0100",
                        "old_value": 0.2504515403888889,
                        "new_value": 0.1236755199444445,
                        "difference": -0.1267760204444444
                    },
                    {
                        "id": "FDF0100",
                        "label": "Creamy dressings",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "FDF0100",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "MSC0300",
                        "label": "Fatty sauces",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MSC0300",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    },
                    {
                        "id": "MSC0100",
                        "label": "Gravy",
                        "include_subgroup": true,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "value": 0.1780204324166713,
                        "key": "MSC0100",
                        "old_value": 0.1722500277777778,
                        "new_value": 0.0,
                        "difference": -0.1722500277777778
                    },
                    {
                        "id": "MSC0200",
                        "label": "Reduced fat gravy",
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": false,
                        "value": 0.0,
                        "key": "MSC0200",
                        "old_value": 0.0,
                        "new_value": 0.0,
                        "difference": 0.0
                    }
                ]
            }
        ],
        "increases": [
            {
                "change": {
                    "key": "meta-vegetables",
                    "label": "Vegetables",
                    "parent_info": {
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": true,
                        "children": {
                            "VEG0100": {
                                "id": "VEG0100",
                                "label": "Green Leafy Vegetables",
                                "include_subgroup": true,
                                "suppress_increase": false,
                                "suppress_decrease": true,
                                "value": 0.0
                            },
                            "VEG0200": {
                                "id": "VEG0200",
                                "label": "Deep-yellow Vegetables",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "VEG0300": {
                                "id": "VEG0300",
                                "label": "Tomato",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.2225255405208392
                            },
                            "VEG0400": {
                                "id": "VEG0400",
                                "label": "White Potatoes",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.2079278650626721
                            },
                            "VEG0450": {
                                "id": "VEG0450",
                                "label": "Other Starchy Vegetables",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "VEG0700": {
                                "id": "VEG0700",
                                "label": "Beans",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "VEG0600": {
                                "id": "VEG0600",
                                "label": "Other Vegetables",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.03097555524050081
                            },
                            "MSC0500": {
                                "id": "MSC0500",
                                "label": "Pickled Vegetables",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.08295752150616886
                            }
                        },
                        "label": "Vegetables",
                        "value": 0.5443864823301809
                    },
                    "old_value": 0.5443864823301809,
                    "new_value": 14.42697332655556,
                    "difference": 13.88258684422538,
                    "children": [
                        {
                            "id": "VEG0700",
                            "label": "Beans",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "VEG0700",
                            "old_value": 0.0,
                            "new_value": 1.547838749611111,
                            "difference": 1.547838749611111
                        },
                        {
                            "id": "VEG0200",
                            "label": "Deep-yellow vegetables",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "VEG0200",
                            "old_value": 0.0,
                            "new_value": 0.6890001111111111,
                            "difference": 0.6890001111111111
                        },
                        {
                            "id": "VEG0100",
                            "label": "Green leafy vegetables",
                            "include_subgroup": true,
                            "suppress_increase": false,
                            "suppress_decrease": true,
                            "value": 0.0,
                            "key": "VEG0100",
                            "old_value": 0.0,
                            "new_value": 1.735246779833334,
                            "difference": 1.735246779833334
                        },
                        {
                            "id": "VEG0450",
                            "label": "Other starchy vegetables",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "VEG0450",
                            "old_value": 0.0,
                            "new_value": 0.8970781446666668,
                            "difference": 0.8970781446666668
                        },
                        {
                            "id": "VEG0600",
                            "label": "Other vegetables",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.03097555524050081,
                            "key": "VEG0600",
                            "old_value": 0.02997150483333334,
                            "new_value": 3.8229171165,
                            "difference": 3.792945611666667
                        },
                        {
                            "id": "MSC0500",
                            "label": "Pickled vegetables",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.08295752150616886,
                            "key": "MSC0500",
                            "old_value": 0.08026851294444447,
                            "new_value": 0.0,
                            "difference": -0.08026851294444447
                        },
                        {
                            "id": "VEG0300",
                            "label": "Tomato",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.2225255405208392,
                            "key": "VEG0300",
                            "old_value": 0.2153125347222223,
                            "new_value": 5.734892424833333,
                            "difference": 5.519579890111111
                        },
                        {
                            "id": "VEG0400",
                            "label": "White potatoes",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.2079278650626721,
                            "key": "VEG0400",
                            "old_value": 0.2011880324444444,
                            "new_value": 0.0,
                            "difference": -0.2011880324444444
                        }
                    ]
                },
                "label": "Vegetables"
            },
            {
                "change": {
                    "key": "meta-whole-grains",
                    "label": "Whole grains",
                    "parent_info": {
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": true,
                        "children": {
                            "GRW0100": {
                                "id": "GRW0100",
                                "label": "Grains, Flour and Dry Mixes - Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRW0200": {
                                "id": "GRW0200",
                                "label": "Loaf-type Bread and Plain Rolls - Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRW0300": {
                                "id": "GRW0300",
                                "label": "Other Breads (quick breads, corn muffins, tortillas) - Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRW0400": {
                                "id": "GRW0400",
                                "label": "Crackers - Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRW0500": {
                                "id": "GRW0500",
                                "label": "Pasta - Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRW0600": {
                                "id": "GRW0600",
                                "label": "Ready-to-eat Cereal (not presweetened) - Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRW1100": {
                                "id": "GRW1100",
                                "label": "Popcorn",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRW0700": {
                                "id": "GRW0700",
                                "label": "Ready-to-eat Cereal (presweetened) - Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRW0900": {
                                "id": "GRW0900",
                                "label": "Snack Chips - Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRW1000": {
                                "id": "GRW1000",
                                "label": "Snack Bars - Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            }
                        },
                        "label": "Whole Grains",
                        "value": 0.0
                    },
                    "old_value": 0.0,
                    "new_value": 7.290999175777777,
                    "difference": 7.290999175777777,
                    "children": [
                        {
                            "id": "GRW0400",
                            "label": "Crackers - whole grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRW0400",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRW0100",
                            "label": "Grains, flour and dry mixes - whole grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRW0100",
                            "old_value": 0.0,
                            "new_value": 2.544477410333334,
                            "difference": 2.544477410333334
                        },
                        {
                            "id": "GRW0200",
                            "label": "Loaf-type bread and plain rolls - whole grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRW0200",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRW0500",
                            "label": "Pasta - whole grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRW0500",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRW1100",
                            "label": "Plain popcorn",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRW1100",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRW0300",
                            "label": "Quick and other breads",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRW0300",
                            "old_value": 0.0,
                            "new_value": 4.746521765444444,
                            "difference": 4.746521765444444
                        },
                        {
                            "id": "GRW0600",
                            "label": "Ready-to-eat cereal (not presweetened) - whole grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRW0600",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRW0700",
                            "label": "Ready-to-eat cereal (presweetened) - whole grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRW0700",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRW1000",
                            "label": "Whole grain snack bars",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRW1000",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRW0900",
                            "label": "Whole grain snack chips",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRW0900",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        }
                    ]
                },
                "label": "Whole grains"
            },
            {
                "change": {
                    "key": "meta-healthier-snacks",
                    "label": "Healthier snacks",
                    "parent_info": {
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": true,
                        "children": {
                            "GRW1000": {
                                "id": "GRW1000",
                                "label": "Whole Grain Snack Bars",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRW0900": {
                                "id": "GRW0900",
                                "label": "Whole Grain Snack Chips",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRW1100": {
                                "id": "GRW1100",
                                "label": "Plain Popcorn",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRW0300": {
                                "id": "GRW0300",
                                "label": "Quick and Other Breads",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            }
                        },
                        "label": "Healthier Snacks",
                        "value": 0.0
                    },
                    "old_value": 0.0,
                    "new_value": 4.746521765444444,
                    "difference": 4.746521765444444,
                    "children": [
                        {
                            "id": "GRW1100",
                            "label": "Plain popcorn",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRW1100",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRW0300",
                            "label": "Quick and other breads",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRW0300",
                            "old_value": 0.0,
                            "new_value": 4.746521765444444,
                            "difference": 4.746521765444444
                        },
                        {
                            "id": "GRW1000",
                            "label": "Whole grain snack bars",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRW1000",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRW0900",
                            "label": "Whole grain snack chips",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRW0900",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        }
                    ]
                },
                "label": "Healthier snacks"
            },
            {
                "change": {
                    "key": "meta-unsaturated-healthier-fats",
                    "label": "Unsaturated (healthier) fats",
                    "parent_info": {
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": true,
                        "children": {
                            "FOF0100": {
                                "id": "FOF0100",
                                "label": "Vegetable Oils",
                                "include_subgroup": true,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 1.023261445531027
                            },
                            "FRU0500": {
                                "id": "FRU0500",
                                "label": "Avocados",
                                "include_subgroup": true,
                                "suppress_increase": false,
                                "suppress_decrease": true,
                                "value": 0.0
                            }
                        },
                        "label": "Unsaturated (Healthier) Fats",
                        "value": 1.023261445531027
                    },
                    "old_value": 1.023261445531027,
                    "new_value": 5.373167366500001,
                    "difference": 4.349905920968974,
                    "children": [
                        {
                            "id": "FRU0500",
                            "label": "Avocados",
                            "include_subgroup": true,
                            "suppress_increase": false,
                            "suppress_decrease": true,
                            "value": 0.0,
                            "key": "FRU0500",
                            "old_value": 0.0,
                            "new_value": 0.824044132888889,
                            "difference": 0.824044132888889
                        },
                        {
                            "id": "FOF0100",
                            "label": "Vegetable oils",
                            "include_subgroup": true,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 1.023261445531027,
                            "key": "FOF0100",
                            "old_value": 0.9900931596666669,
                            "new_value": 4.549123233611112,
                            "difference": 3.559030073944445
                        }
                    ]
                },
                "label": "Unsaturated (healthier) fats"
            },
            {
                "change": {
                    "key": "meta-fruit",
                    "label": "Fruit",
                    "parent_info": {
                        "include_subgroup": false,
                        "suppress_increase": false,
                        "suppress_decrease": true,
                        "children": {
                            "FRU0300": {
                                "id": "FRU0300",
                                "label": "Citrus Fruit",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": true,
                                "value": 0.0
                            },
                            "FRU0400": {
                                "id": "FRU0400",
                                "label": "Fruit excluding Citrus Fruit",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            }
                        },
                        "label": "Fruit",
                        "value": 0.0
                    },
                    "old_value": 0.0,
                    "new_value": 3.946592636444445,
                    "difference": 3.946592636444445,
                    "children": [
                        {
                            "id": "FRU0300",
                            "label": "Citrus fruit",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": true,
                            "value": 0.0,
                            "key": "FRU0300",
                            "old_value": 0.0,
                            "new_value": 0.1708720275555556,
                            "difference": 0.1708720275555556
                        },
                        {
                            "id": "FRU0400",
                            "label": "Fruit excluding citrus fruit",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "FRU0400",
                            "old_value": 0.0,
                            "new_value": 3.77572060888889,
                            "difference": 3.77572060888889
                        }
                    ]
                },
                "label": "Fruit"
            }
        ],
        "decreases": [
            {
                "change": {
                    "key": "meta-sweets-desserts",
                    "label": "Sweets & desserts",
                    "parent_info": {
                        "include_subgroup": false,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "children": {
                            "MSC0600": {
                                "id": "MSC0600",
                                "label": "Misc",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "DOT0100": {
                                "id": "DOT0100",
                                "label": "Frozen Dairy Dessert",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "DOT0200": {
                                "id": "DOT0200",
                                "label": "Frozen Nondairy Dessert",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.3111797158643415
                            },
                            "DOT0300": {
                                "id": "DOT0300",
                                "label": "Pudding and Other Dairy Dessert",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "DOT0400": {
                                "id": "DOT0400",
                                "label": "Artificially Sweetened Pudding and Other Dairy Dessert",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRW0800": {
                                "id": "GRW0800",
                                "label": "Whole Grain Cookies, Pies, Pastries, Danish, etc.",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRS0800": {
                                "id": "GRS0800",
                                "label": "Some Whole Grain Cookies, Pies, Pastries, Danish, etc.",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRR0800": {
                                "id": "GRR0800",
                                "label": "Cookies, Pies, Pastries, Danish, etc.",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 2.598386231553735
                            },
                            "GRS1000": {
                                "id": "GRS1000",
                                "label": "Snack Bars Some Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRR1000": {
                                "id": "GRR1000",
                                "label": "Snack Bars",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRR0300": {
                                "id": "GRR0300",
                                "label": "Refined Snack Breads",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "SWT0500": {
                                "id": "SWT0500",
                                "label": "Syrup, Honey, Jam",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.1619985934991709
                            },
                            "SWT0400": {
                                "id": "SWT0400",
                                "label": "Sugar",
                                "include_subgroup": true,
                                "suppress_increase": true,
                                "suppress_decrease": false,
                                "value": 3.797531864312433
                            },
                            "SWT0700": {
                                "id": "SWT0700",
                                "label": "Fudge, caramel, etc.",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "SWT0100": {
                                "id": "SWT0100",
                                "label": "Chocolate Candy",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.1424163459333371
                            },
                            "SWT0200": {
                                "id": "SWT0200",
                                "label": "Candy",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.7729647175531869
                            },
                            "SWT0300": {
                                "id": "SWT0300",
                                "label": "Frosting or icing",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            }
                        },
                        "label": "Sweets & Desserts",
                        "value": 7.784477468716204
                    },
                    "old_value": 7.784477468716204,
                    "new_value": 0.0,
                    "difference": -7.784477468716204,
                    "children": [
                        {
                            "id": "DOT0400",
                            "label": "Artificially sweetened pudding and other dairy dessert",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "DOT0400",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "SWT0200",
                            "label": "Candy",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.7729647175531869,
                            "key": "SWT0200",
                            "old_value": 0.7479096206111111,
                            "new_value": 0.0,
                            "difference": -0.7479096206111111
                        },
                        {
                            "id": "SWT0100",
                            "label": "Chocolate candy",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.1424163459333371,
                            "key": "SWT0100",
                            "old_value": 0.1378000222222222,
                            "new_value": 0.0,
                            "difference": -0.1378000222222222
                        },
                        {
                            "id": "GRR0800",
                            "label": "Cookies, pies, pastries, danish, etc.",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 2.598386231553735,
                            "key": "GRR0800",
                            "old_value": 2.514161405444445,
                            "new_value": 0.0,
                            "difference": -2.514161405444445
                        },
                        {
                            "id": "SWT0300",
                            "label": "Frosting or icing",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "SWT0300",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "DOT0100",
                            "label": "Frozen dairy dessert",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "DOT0100",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "DOT0200",
                            "label": "Frozen nondairy dessert",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.3111797158643415,
                            "key": "DOT0200",
                            "old_value": 0.3010930485555556,
                            "new_value": 0.0,
                            "difference": -0.3010930485555556
                        },
                        {
                            "id": "SWT0700",
                            "label": "Fudge, caramel, butterscotch, etc.",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "SWT0700",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "MSC0600",
                            "label": "Misc",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "MSC0600",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "DOT0300",
                            "label": "Pudding and other dairy dessert",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "DOT0300",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRR0300",
                            "label": "Refined snack breads",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRR0300",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRR1000",
                            "label": "Snack bars",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRR1000",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRS1000",
                            "label": "Snack bars some whole grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRS1000",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRS0800",
                            "label": "Some whole grain cookies, pies, pastries, danish, etc.",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRS0800",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "SWT0400",
                            "label": "Sugar",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 3.797531864312433,
                            "key": "SWT0400",
                            "old_value": 3.674437592555556,
                            "new_value": 0.0,
                            "difference": -3.674437592555556
                        },
                        {
                            "id": "SWT0500",
                            "label": "Syrup, honey, jam",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.1619985934991709,
                            "key": "SWT0500",
                            "old_value": 0.1567475252777778,
                            "new_value": 0.0,
                            "difference": -0.1567475252777778
                        },
                        {
                            "id": "GRW0800",
                            "label": "Whole grain cookies, pies, pastries, danish, etc.",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRW0800",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        }
                    ]
                },
                "label": "Sweets & desserts"
            },
            {
                "change": {
                    "key": "meta-sweet-salty-condiments",
                    "label": "Sweet / salty condiments",
                    "parent_info": {
                        "include_subgroup": false,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "children": {
                            "MSC0400": {
                                "id": "MSC0400",
                                "label": "Sweet/Salty Sauces",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 3.306907552572087
                            },
                            "SWT0500": {
                                "id": "SWT0500",
                                "label": "Syrup, Honey, Jam",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.1619985934991709
                            },
                            "SWT0400": {
                                "id": "SWT0400",
                                "label": "Sugar",
                                "include_subgroup": true,
                                "suppress_increase": true,
                                "suppress_decrease": false,
                                "value": 3.797531864312433
                            },
                            "SWT0700": {
                                "id": "SWT0700",
                                "label": "Fudge, caramel, butterscotch, etc.",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "SWT0300": {
                                "id": "SWT0300",
                                "label": "Frosting or icing",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            }
                        },
                        "label": "Sweet / Salty Condiments",
                        "value": 7.266438010383692
                    },
                    "old_value": 7.266438010383692,
                    "new_value": 0.4592185740555556,
                    "difference": -6.807219436328136,
                    "children": [
                        {
                            "id": "SWT0300",
                            "label": "Frosting or icing",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "SWT0300",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "SWT0700",
                            "label": "Fudge, caramel, butterscotch, etc.",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "SWT0700",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "SWT0400",
                            "label": "Sugar",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 3.797531864312433,
                            "key": "SWT0400",
                            "old_value": 3.674437592555556,
                            "new_value": 0.0,
                            "difference": -3.674437592555556
                        },
                        {
                            "id": "MSC0400",
                            "label": "Sweet/salty sauces",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 3.306907552572087,
                            "key": "MSC0400",
                            "old_value": 3.1997165160000005,
                            "new_value": 0.4592185740555556,
                            "difference": -2.740497941944445
                        },
                        {
                            "id": "SWT0500",
                            "label": "Syrup, honey, jam",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.1619985934991709,
                            "key": "SWT0500",
                            "old_value": 0.1567475252777778,
                            "new_value": 0.0,
                            "difference": -0.1567475252777778
                        }
                    ]
                },
                "label": "Sweet / salty condiments"
            },
            {
                "change": {
                    "key": "meta-refined-grains",
                    "label": "Refined grains",
                    "parent_info": {
                        "include_subgroup": false,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "children": {
                            "GRS0100": {
                                "id": "GRS0100",
                                "label": "Grains, Flour and Dry Mixes - Some Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRR0100": {
                                "id": "GRR0100",
                                "label": "Grains, Flour and Dry Mixes - Refined Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 1.256468211996866
                            },
                            "GRS0200": {
                                "id": "GRS0200",
                                "label": "Loaf-type Bread and Plain Rolls - Some Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRR0200": {
                                "id": "GRR0200",
                                "label": "Loaf-type Bread and Plain Rolls - Refined Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 2.500474993724565
                            },
                            "GRS0300": {
                                "id": "GRS0300",
                                "label": "Other Breads (quick breads, corn muffins, tortillas) - Some Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRR0300": {
                                "id": "GRR0300",
                                "label": "Other Breads (quick breads, corn muffins, tortillas) - Refined Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRS0400": {
                                "id": "GRS0400",
                                "label": "Crackers - Some Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRR0400": {
                                "id": "GRR0400",
                                "label": "Crackers - Refined Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRS0500": {
                                "id": "GRS0500",
                                "label": "Pasta - Some Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRR0500": {
                                "id": "GRR0500",
                                "label": "Pasta - Refined Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.4069547085045107
                            },
                            "GRS0600": {
                                "id": "GRS0600",
                                "label": "Ready-to-eat Cereal (not presweetened) - Some Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRR0600": {
                                "id": "GRR0600",
                                "label": "Ready-to-eat Cereal (not presweetened) - Refined Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRS0700": {
                                "id": "GRS0700",
                                "label": "Ready-to-eat Cereal (presweetened) - Some Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRR0700": {
                                "id": "GRR0700",
                                "label": "Ready-to-eat Cereal (presweetened) - Refined Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRS0800": {
                                "id": "GRS0800",
                                "label": "Cakes, Cookies, Pies, Pastries, Danish, Doughnuts and Cobblers - Some Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRR0800": {
                                "id": "GRR0800",
                                "label": "Cakes, Cookies, Pies, Pastries, Danish, Doughnuts and Cobblers - Refined Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 2.598386231553735
                            },
                            "GRS1000": {
                                "id": "GRS1000",
                                "label": "Snack Bars - Some Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRR1000": {
                                "id": "GRR1000",
                                "label": "Snack Bars - Refined Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRS0900": {
                                "id": "GRS0900",
                                "label": "Snack Chips - Some Whole Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "GRR0900": {
                                "id": "GRR0900",
                                "label": "Snack Chips - Refined Grain",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            }
                        },
                        "label": "Refined Grains",
                        "value": 6.762284145779677
                    },
                    "old_value": 6.762284145779677,
                    "new_value": 0.0,
                    "difference": -6.762284145779677,
                    "children": [
                        {
                            "id": "GRR0800",
                            "label": "Cookies, pies, pastries, danish, etc.",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 2.598386231553735,
                            "key": "GRR0800",
                            "old_value": 2.514161405444445,
                            "new_value": 0.0,
                            "difference": -2.514161405444445
                        },
                        {
                            "id": "GRR0400",
                            "label": "Crackers - refined grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRR0400",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRS0400",
                            "label": "Crackers - some whole grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRS0400",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRR0100",
                            "label": "Grains, flour and dry mixes - refined grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 1.256468211996866,
                            "key": "GRR0100",
                            "old_value": 1.215740696055556,
                            "new_value": 0.0,
                            "difference": -1.215740696055556
                        },
                        {
                            "id": "GRS0100",
                            "label": "Grains, flour and dry mixes - some whole grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRS0100",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRR0200",
                            "label": "Loaf-type bread and plain rolls - refined grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 2.500474993724565,
                            "key": "GRR0200",
                            "old_value": 2.419423890166667,
                            "new_value": 0.0,
                            "difference": -2.419423890166667
                        },
                        {
                            "id": "GRS0200",
                            "label": "Loaf-type bread and plain rolls - some whole grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRS0200",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRS0300",
                            "label": "Other breads (quick breads, corn muffins, tortillas) - some whole grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRS0300",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRR0500",
                            "label": "Pasta - refined grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.4069547085045107,
                            "key": "GRR0500",
                            "old_value": 0.3937635635,
                            "new_value": 0.0,
                            "difference": -0.3937635635
                        },
                        {
                            "id": "GRS0500",
                            "label": "Pasta - some whole grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRS0500",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRR0600",
                            "label": "Ready-to-eat cereal (not presweetened) - refined grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRR0600",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRS0600",
                            "label": "Ready-to-eat cereal (not presweetened) - some whole grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRS0600",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRR0700",
                            "label": "Ready-to-eat cereal (presweetened) - refined grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRR0700",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRS0700",
                            "label": "Ready-to-eat cereal (presweetened) - some whole grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRS0700",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRR0300",
                            "label": "Refined snack breads",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRR0300",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRR1000",
                            "label": "Snack bars",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRR1000",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRS1000",
                            "label": "Snack bars some whole grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRS1000",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRR0900",
                            "label": "Snack chips",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRR0900",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRS0900",
                            "label": "Snack chips some whole grain",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRS0900",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "GRS0800",
                            "label": "Some whole grain cookies, pies, pastries, danish, etc.",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "GRS0800",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        }
                    ]
                },
                "label": "Refined grains"
            },
            {
                "change": {
                    "key": "meta-sweetened-beverages",
                    "label": "Sweetened beverages",
                    "parent_info": {
                        "include_subgroup": false,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "children": {
                            "BVS0400": {
                                "id": "BVS0400",
                                "label": "Soda",
                                "include_subgroup": true,
                                "suppress_increase": true,
                                "suppress_decrease": false,
                                "value": 2.67030648625007
                            },
                            "BVS0300": {
                                "id": "BVS0300",
                                "label": "Sweetened Fruit Drinks",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "BVS0500": {
                                "id": "BVS0500",
                                "label": "Sweetened Tea",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "BVS0100": {
                                "id": "BVS0100",
                                "label": "Sweetened Coffee",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "BVS0200": {
                                "id": "BVS0200",
                                "label": "Sweetened Coffee Substitutes",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "BVS0600": {
                                "id": "BVS0600",
                                "label": "Sweetened Water",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "BVO0100": {
                                "id": "BVO0100",
                                "label": "Non-alcoholic Beer",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "BVO0200": {
                                "id": "BVO0200",
                                "label": "Non-alcoholic Light Beer",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            }
                        },
                        "label": "Sweetened Beverages",
                        "value": 2.67030648625007
                    },
                    "old_value": 2.67030648625007,
                    "new_value": 0.0,
                    "difference": -2.67030648625007,
                    "children": [
                        {
                            "id": "BVO0100",
                            "label": "Non-alcoholic beer",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "BVO0100",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "BVO0200",
                            "label": "Non-alcoholic light beer",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "BVO0200",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "BVS0400",
                            "label": "Soda",
                            "include_subgroup": true,
                            "suppress_increase": true,
                            "suppress_decrease": false,
                            "value": 2.67030648625007,
                            "key": "BVS0400",
                            "old_value": 2.583750416666667,
                            "new_value": 0.0,
                            "difference": -2.583750416666667
                        },
                        {
                            "id": "BVS0100",
                            "label": "Sweetened coffee",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "BVS0100",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "BVS0200",
                            "label": "Sweetened coffee substitutes",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "BVS0200",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "BVS0300",
                            "label": "Sweetened fruit drinks",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "BVS0300",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "BVS0500",
                            "label": "Sweetened tea",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "BVS0500",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "BVS0600",
                            "label": "Sweetened water",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "BVS0600",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        }
                    ]
                },
                "label": "Sweetened beverages"
            },
            {
                "change": {
                    "key": "meta-processed-meat",
                    "label": "Processed meat",
                    "parent_info": {
                        "include_subgroup": false,
                        "suppress_increase": true,
                        "suppress_decrease": false,
                        "children": {
                            "MCF0100": {
                                "id": "MCF0100",
                                "label": "Cold Cuts and Sausage",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 1.840375230323548
                            },
                            "MCL0100": {
                                "id": "MCL0100",
                                "label": "Lean Cold Cuts and Sausage",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.07120817296666854
                            },
                            "MCF0200": {
                                "id": "MCF0200",
                                "label": "Cured Pork",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.2720152207326738
                            },
                            "MCL0200": {
                                "id": "MCL0200",
                                "label": "Lean Cured Pork",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            },
                            "FMC0200": {
                                "id": "FMC0200",
                                "label": "Meat-based Savory Snack",
                                "include_subgroup": false,
                                "suppress_increase": false,
                                "suppress_decrease": false,
                                "value": 0.0
                            }
                        },
                        "label": "Processed Meat",
                        "value": 2.183598624022891
                    },
                    "old_value": 2.183598624022891,
                    "new_value": 0.0,
                    "difference": -2.183598624022891,
                    "children": [
                        {
                            "id": "MCF0100",
                            "label": "Cold cuts and sausage",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 1.840375230323548,
                            "key": "MCF0100",
                            "old_value": 1.780720787166667,
                            "new_value": 0.0,
                            "difference": -1.780720787166667
                        },
                        {
                            "id": "MCF0200",
                            "label": "Cured pork",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.2720152207326738,
                            "key": "MCF0200",
                            "old_value": 0.2631980424444444,
                            "new_value": 0.0,
                            "difference": -0.2631980424444444
                        },
                        {
                            "id": "MCL0100",
                            "label": "Lean cold cuts and sausage",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.07120817296666854,
                            "key": "MCL0100",
                            "old_value": 0.06890001111111112,
                            "new_value": 0.0,
                            "difference": -0.06890001111111112
                        },
                        {
                            "id": "MCL0200",
                            "label": "Lean cured pork",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "MCL0200",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        },
                        {
                            "id": "FMC0200",
                            "label": "Meat based savory snack (pork rinds)",
                            "include_subgroup": false,
                            "suppress_increase": false,
                            "suppress_decrease": false,
                            "value": 0.0,
                            "key": "FMC0200",
                            "old_value": 0.0,
                            "new_value": 0.0,
                            "difference": 0.0
                        }
                    ]
                },
                "label": "Processed meat"
            }
        ]
    }
}`}
            />
          </Grid>
          <Grid
            item
            xs={2}
          >
            <h3>Error Response</h3>
          </Grid>
          <Grid
            item
            xs={10}
          >
            <CodeHighlighter
              code={`{
    "success": "false",
    "message": "Must be logged in"
}
  `}
            />
          </Grid>
        </Grid>
      </Typography>
    </AccordionDetails>
  );
};
const Wrapper = () => {
  return (
    <React.Suspense fallback={<AdminProgressBar />}>
      <DietsCompare />
    </React.Suspense>
  );
};
export default Wrapper;
